import { useEffect, useState } from "react";
import api from "@/lib/api";
import { UserSwitch, User, PlusCircle, DownloadSimple, ArrowRight } from "@phosphor-icons/react";

const TYPE_META = {
  created:          { label: "Created",           tone: "bg-emerald-50 text-emerald-800 border-emerald-200", Icon: PlusCircle },
  imported_initial: { label: "Imported (Initial)", tone: "bg-slate-50 text-slate-700 border-slate-200",     Icon: DownloadSimple },
  reassigned:       { label: "Reassigned",        tone: "bg-amber-50 text-amber-800 border-amber-200",     Icon: UserSwitch },
  automatic:        { label: "Auto-assigned",     tone: "bg-sky-50 text-sky-800 border-sky-200",           Icon: User },
  manual:           { label: "Assigned",          tone: "bg-sky-50 text-sky-800 border-sky-200",           Icon: User },
};

function fmt(dt) {
  if (!dt) return "—";
  try { return new Date(dt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return dt; }
}

export default function OwnershipTimeline({ candidateId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!candidateId) return;
    api.get(`/candidates/${candidateId}/ownership-history`)
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRows([]));
  }, [candidateId]);

  if (rows === null) {
    return <div className="text-xs text-slate-400" data-testid="ownership-timeline-loading">Loading ownership history…</div>;
  }
  if (rows.length === 0) {
    return (
      <div className="border border-dashed border-slate-200 rounded-lg p-3 text-xs text-slate-500"
           data-testid="ownership-timeline-empty">
        No ownership history recorded for this candidate yet.
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="ownership-timeline">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Ownership Timeline</div>
        <div className="text-[11px] text-slate-400">{rows.length} {rows.length === 1 ? "entry" : "entries"} · append-only</div>
      </div>
      <ol className="relative border-l border-slate-200 pl-4 space-y-3">
        {rows.map((r, idx) => {
          const meta = TYPE_META[r.assignment_type] || { label: r.assignment_type || "Update",
                                                         tone: "bg-slate-50 text-slate-700 border-slate-200",
                                                         Icon: User };
          const Icon = meta.Icon;
          return (
            <li key={r.id || idx}
                data-testid={`ownership-timeline-row-${r.assignment_type || "unknown"}`}
                className="relative">
              <span className="absolute -left-[22px] top-0.5 w-4 h-4 rounded-full bg-white border-2 border-slate-300 grid place-items-center">
                <Icon size={9} className="text-slate-500" />
              </span>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className={`text-[11px] px-2 py-0.5 rounded-full border font-medium ${meta.tone}`}>
                  {meta.label}
                </span>
                <span className="font-medium text-slate-800">{r.owner_name || "—"}</span>
                {r.previous_owner_id && (
                  <span className="text-xs text-slate-500 inline-flex items-center gap-1">
                    <ArrowRight size={11} /> from prior owner
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {fmt(r.changed_at)}
                {r.changed_by_name ? <> · by <span className="text-slate-700">{r.changed_by_name}</span></> : null}
                {r.reason ? <> · <span className="italic">{r.reason}</span></> : null}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
